import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import pipefitterApi from '../../../services/pipefitterApi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { projectSummaryHelper } from '../helpers/reportHelpers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Label } from '../../../components/ui/label';
import {
  PIPEFITTER_GET_ALL_PIPE_DATA,
  PIPEFITTER_FETCH_PROJECT_REPORT,
  PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
} from '../../../redux/Pipefitter/types';
import { ArrowLeft, Loader2, Search, Database, Link2, Download, Trash2, FileText, History, Edit } from 'lucide-react';
import type { DashboardProject } from '../../project/types/project';
import { PIPEFITTER_TAB_TRIGGER_CLASS } from '../constants/uiConstants';
import { cn } from '../../../lib/utils';
import { Reports } from './Reports';
import {
  pipefitterGenerateReportPipeData,
  pipefitterGenerateReportProjectSummary,
  pipefitterTriggerReportComplimentary,
} from '../../../redux/Pipefitter/actions';
import { ProjectStatus } from './ProjectStatus';
import { PipeData } from './PipeData';
import { PipeCompatibility } from './PipeCompatibility';
import { getReportVersions, isReportVersionsFailure, normalizeReportVersionRow, type ReportVersionRow } from '../api/reportVersions';
import { PIPEFITTER_REPORT_TYPES, type PipefitterReportType } from '../constants/reportVersionTypes';
import { PipefitterReportVersionsModal } from './PipefitterReportVersionsModal';
import { loadVisualizationReportHistory } from '../api/reportHistory';

export type ReportTypeTab = 'pipeData' | 'pipeCompatibility';

const REPORT_MODAL_TABS = [
  { key: 'pipeData' as const, label: 'Pipe Data', shortLabel: 'Data', icon: Database },
  { key: 'pipeCompatibility' as const, label: 'Pipe Compatibility', shortLabel: 'Compat', icon: Link2 },
];

const REPORT_VERSIONS_TABS: Array<{
  key: ReportTypeTab;
  label: string;
  backendType: PipefitterReportType;
}> = [
    { key: 'pipeData', label: 'Pipe Data', backendType: PIPEFITTER_REPORT_TYPES.PIPE_DATA },
    { key: 'pipeCompatibility', label: 'Pipe Compatibility', backendType: PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY },
  ];

export interface FileVersion {
  id: number;
  fileUrl: string;
  fileName: string;
  createdAt?: string;
}

export interface ReportHistoryItem {
  /** Local UUID used as React key before the server assigns an id */
  id: string;
  /** Server-assigned numeric id — set after a successful POST */
  serverId?: number;
  reportType: ReportTypeTab;
  reportName: string;
  generatedAt: Date;
  generatedBy: string;
  pipeCount: number;
  /** Array of pipe objects carrying both the display name and internal ID */
  pipeIds: { pipeEndId: string; id: string | number }[];
  status: 'Started' | 'Processing' | 'Completed' | 'Failed';
  /** Latest S3 URL — derived from fileUrls[last] */
  fileUrl?: string;
  /** All file versions returned by the API */
  fileUrls?: FileVersion[];
}

export interface PipeListItem {
  id: string;
  pipeID: string;
}

interface PipefitterReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: DashboardProject;
  selectedPipe: { pipeId: string; displayName?: string; pipeName?: string } | null;
  canReport?: boolean;
}

function formatReportDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getNextReportSequence(): number {
  const today = new Date().toISOString().slice(0, 10);
  const key = `pf_report_seq_${today}`;
  const current = parseInt(localStorage.getItem(key) || '0', 10);
  const next = current + 1;
  localStorage.setItem(key, String(next));
  return next;
}

function generateReportName(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const seq = getNextReportSequence();
  return `RPT-${year}-${month}${day}-${String(seq).padStart(3, '0')}`;
}

function getUsernameFromStorage(): string {
  try {
    const raw =
      localStorage.getItem('user') ||
      localStorage.getItem('currentUser') ||
      localStorage.getItem('userData');
    if (raw) {
      const parsed = JSON.parse(raw);
      return (
        parsed.username ||
        parsed.name ||
        parsed.displayName ||
        parsed.email ||
        'Unknown User'
      );
    }
    const direct =
      localStorage.getItem('username') || localStorage.getItem('userName');
    if (direct) return direct;
  } catch {
    /* ignore */
  }
  return 'Unknown User';
}

// ── Report History API ────────────────────────────────────────────────────────

const REPORT_TYPE_TO_API: Record<string, string> = {
  pipeData: 'PIPE_DATA',
  pipeCompatibility: 'PIPE_COMPATIBILITY',
};

interface ReportHistoryApiPayload {
  id?: number;
  reportType: string;
  reportName: string;
  generatedAt: string;
  generatedBy: string;
  pipeCount: number;
  pipeIds: { pipeEndId: string; id: string | number }[];
  status: string;
  fileUrl?: string;
  pipeId?: string | number;
  projectId?: string | number;
  projectNumber: string;
}

async function fetchReportHistoryApi(
  projectNumber: string,
  projectId?: string | number,
  pipeId?: string | number
): Promise<ReportHistoryItem[]> {
  const rows = await loadVisualizationReportHistory({ projectNumber, projectId, pipeId });
  return rows.map((r) => ({
    ...r,
    fileUrls: r.fileUrls?.map((f, index) => ({
      id: f.docRef ?? index,
      fileUrl: f.fileUrl,
      fileName: f.fileName ?? 'Report.pdf',
      createdAt: undefined,
    })),
  }));
}

async function saveReportHistoryApi(
  payload: ReportHistoryApiPayload
): Promise<number | undefined> {
  const { data } = await pipefitterApi.post('/reportgen/report-history', payload);
  if (data?.status !== 200) return undefined;
  const record = Array.isArray(data.data) ? data.data[0] : data.data;
  return record?.id;
}

async function deleteReportHistoryApi(ids: number[]): Promise<boolean> {
  const { data } = await pipefitterApi.post('/reportgen/report-history-delete', { ids });
  return data?.status === 200;
}

export const PipefitterReportsModal: React.FC<PipefitterReportsModalProps> = ({
  isOpen,
  onClose,
  project,
  selectedPipe,
  canReport = true,
}) => {
  const dispatch = useDispatch();
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectId = project?.project?.projectId;
  const projectName = project?.project?.projectName ?? '';
  const DEMO_PROJECT_NUMBER = process.env.REACT_APP_PIPEFITTER_DEMO_PROJECT_NUMBER ?? '';

  const reportsState = useSelector((state: any) => state.pipefitterReports || {});
  const pipeStatusDataRaw = reportsState.pipeStatusData;
  const pipeStatusData = Array.isArray(pipeStatusDataRaw)
    ? pipeStatusDataRaw
    : pipeStatusDataRaw
      ? [pipeStatusDataRaw]
      : [];
  const isReportsLoading = reportsState.isReportsLoading ?? false;
  const projectStatusPipeData = reportsState.projectStatusPipeData ?? [];
  const pipeStatistics = reportsState.pipeStatistics ?? {};
  const pipeDataGraph = reportsState.pipeDataGraph ?? [];
  const projectSummaryData = reportsState.projectSummaryData ?? {};
  const [activeTab, setActiveTab] = useState<ReportTypeTab>('pipeData');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPipeIds, setSelectedPipeIds] = useState<Set<string>>(new Set());
  const [reportHistory, setReportHistory] = useState<ReportHistoryItem[]>([]);
  const [historyFetchError, setHistoryFetchError] = useState<string | null>(null);
  const [editingReport, setEditingReport] = useState<ReportHistoryItem | null>(null);
  const [downloadFilesReport, setDownloadFilesReport] = useState<ReportHistoryItem | null>(null);
  const [deleteConfirmReport, setDeleteConfirmReport] = useState<ReportHistoryItem | null>(null);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [editReportName, setEditReportName] = useState('');
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [viewReportId, setViewReportId] = useState<string | null>(null);
  const [showComplimentaryStartedModal, setShowComplimentaryStartedModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateSummaryModal, setShowGenerateSummaryModal] = useState(false);
  const [summaryReportType, setSummaryReportType] = useState<ReportTypeTab>('pipeData');
  const [showGraphicalView, setShowGraphicalView] = useState(false);
  const selectionSeededForOpenRef = useRef(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [reportVersionsRefreshKey, setReportVersionsRefreshKey] = useState(0);
  const [activeVersionsTab, setActiveVersionsTab] = useState<ReportTypeTab>('pipeData');
  const [versionsByTab, setVersionsByTab] = useState<Record<ReportTypeTab, ReportVersionRow[]>>({
    pipeData: [],
    pipeCompatibility: [],
  });
  const [versionsLoadingByTab, setVersionsLoadingByTab] = useState<Record<ReportTypeTab, boolean>>({
    pipeData: false,
    pipeCompatibility: false,
  });
  const [versionsErrorByTab, setVersionsErrorByTab] = useState<Record<ReportTypeTab, string | null>>({
    pipeData: null,
    pipeCompatibility: null,
  });

  // Generate-modal local state
  const [genModalPipeSearch, setGenModalPipeSearch] = useState('');
  const [genModalSelectedPipeIds, setGenModalSelectedPipeIds] = useState<Set<string>>(new Set());
  const [genIncludePipeData, setGenIncludePipeData] = useState(true);
  const [genIncludeCompatibility, setGenIncludeCompatibility] = useState(false);
  const [isReopenMode, setIsReopenMode] = useState(false);
  const [reopeningReport, setReopeningReport] = useState<ReportHistoryItem | null>(null);

  // Compatibility section state
  const [showCompatFullView, setShowCompatFullView] = useState(false);
  const [compatViewMode, setCompatViewMode] = useState<'overall' | 'individual'>('overall');
  const [compatAlgoTab, setCompatAlgoTab] = useState<'omnifit' | 'optifit' | 'random'>('omnifit');

  const pipeList: PipeListItem[] = useMemo(
    () =>
      pipeStatusData.map((pipe: any) => ({
        id: pipe.id ?? pipe.pipeStatusId ?? pipe.pipeID,
        pipeID: pipe.pipeID ?? pipe.pipeId ?? String(pipe.id),
      })),
    [pipeStatusData]
  );

  const filteredPipeList = useMemo(() => {
    if (!searchQuery.trim()) return pipeList;
    const q = searchQuery.trim().toLowerCase();
    return pipeList.filter((p) => p.pipeID.toLowerCase().includes(q));
  }, [pipeList, searchQuery]);

  const isAllSelected =
    filteredPipeList.length > 0 &&
    filteredPipeList.every((p) => selectedPipeIds.has(p.id));
  const isSomeSelected =
    filteredPipeList.some((p) => selectedPipeIds.has(p.id)) && !isAllSelected;

  const selectedCount = selectedPipeIds.size;

  useEffect(() => {
    if (!isOpen) return;
    const pipeId = selectedPipe?.pipeId;
    const effectiveProjectNumber = projectNumber || (process.env.REACT_APP_PIPEFITTER_DEMO_PROJECT_NUMBER ?? '');
    if (!effectiveProjectNumber || !pipeId) return;
    dispatch({
      type: PIPEFITTER_GET_ALL_PIPE_DATA,
      payload: { projectNumber: effectiveProjectNumber, pipeId },
    });
  }, [isOpen, projectNumber, selectedPipe?.pipeId, dispatch]);

  const handleApplyFilters = (apply: boolean) => {
    const pipeId = selectedPipe?.pipeId;
    const effectiveProjectNumber = projectNumber || DEMO_PROJECT_NUMBER;
    if (apply && effectiveProjectNumber && pipeId) {
      dispatch({
        type: PIPEFITTER_GET_ALL_PIPE_DATA,
        payload: {
          projectNumber: effectiveProjectNumber,
          pipeId,
          selectedPipeIds: Array.from(selectedPipeIds),
        },
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setViewReportId(null);
      setSearchQuery('');
      setShowComplimentaryStartedModal(false);
      selectionSeededForOpenRef.current = false;
    }
  }, [isOpen]);

  // Fetch persisted report history scoped to project + selected pipe size
  useEffect(() => {
    if (!isOpen || !projectNumber) return;
    const materialPipeId = selectedPipe?.pipeId;
    let cancelled = false;
    setIsHistoryLoading(true);
    setHistoryFetchError(null);
    fetchReportHistoryApi(projectNumber, projectId, materialPipeId)
      .then((rows) => {
        if (!cancelled) {
          setReportHistory(rows);
          setIsHistoryLoading(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setIsHistoryLoading(false);
          const status = err?.response?.status ?? err?.status;
          if (status === 410) {
            // Endpoint not yet available or resource gone — treat as empty list
            setReportHistory([]);
            setHistoryFetchError(null);
          } else {
            setHistoryFetchError(
              err?.message ?? 'Failed to load report history. Please try again.'
            );
          }
        }
      });
    return () => { cancelled = true; };
  }, [isOpen, projectNumber, projectId, selectedPipe?.pipeId]);



  const handleConfirmGenerate = () => {
    setShowGenerateSummaryModal(false);
    setIsReopenMode(false);
    setReopeningReport(null);
    if (genIncludePipeData) {
      handleGenerateReport('pipeData', genModalPipeIdsForApi, genModalPipeDisplayIds);
    }
    if (genIncludeCompatibility) {
      handleGenerateReport('pipeCompatibility', genModalPipeIdsForApi, genModalPipeDisplayIds);
    }
  };

  /** Selected pipes, or all loaded pipes when nothing is selected */
  const pipeIdsForReport = useMemo(() => {
    if (selectedPipeIds.size > 0) return Array.from(selectedPipeIds);
    return pipeList.map((p) => p.id);
  }, [selectedPipeIds, pipeList]);

  /** Pipe objects { pipeEndId, id } resolved at report-creation time */
  const pipeDisplayIdsForReport = useMemo(
    () =>
      pipeIdsForReport.map((id) => ({
        pipeEndId: pipeList.find((p) => p.id === id)?.pipeID ?? String(id),
        id,
      })),
    [pipeIdsForReport, pipeList]
  );

  // Seed modal pipe selection from main sidebar whenever the modal opens
  useEffect(() => {
    if (showGenerateSummaryModal && !isReopenMode) {
      setGenModalSelectedPipeIds(new Set(selectedPipeIds));
      setGenModalPipeSearch('');
    }
  }, [showGenerateSummaryModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const genModalFilteredPipeList = useMemo(() => {
    if (!genModalPipeSearch.trim()) return pipeList;
    const q = genModalPipeSearch.trim().toLowerCase();
    return pipeList.filter((p) => p.pipeID.toLowerCase().includes(q));
  }, [pipeList, genModalPipeSearch]);

  const genModalIsAllSelected =
    genModalFilteredPipeList.length > 0 &&
    genModalFilteredPipeList.every((p) => genModalSelectedPipeIds.has(p.id));
  const genModalIsSomeSelected =
    genModalFilteredPipeList.some((p) => genModalSelectedPipeIds.has(p.id)) &&
    !genModalIsAllSelected;
  const genModalSelectedCount = genModalSelectedPipeIds.size;


  const handleGenModalSelectAll = (checked: boolean) => {
    if (checked) {
      setGenModalSelectedPipeIds(new Set(genModalFilteredPipeList.map((p) => p.id)));
    } else {
      const next = new Set(genModalSelectedPipeIds);
      genModalFilteredPipeList.forEach((p) => next.delete(p.id));
      setGenModalSelectedPipeIds(next);
    }
  };

  const handleGenModalTogglePipe = (id: string, checked: boolean) => {
    setGenModalSelectedPipeIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /** Pipe IDs to pass to the API when generating from the modal */
  const genModalPipeIdsForApi = useMemo(
    () =>
      genModalSelectedPipeIds.size > 0
        ? Array.from(genModalSelectedPipeIds)
        : pipeList.map((p) => p.id),
    [genModalSelectedPipeIds, pipeList]
  );

  const genModalPipeDisplayIds = useMemo(
    () =>
      genModalPipeIdsForApi.map((id) => ({
        pipeEndId: pipeList.find((p) => p.id === id)?.pipeID ?? String(id),
        id,
      })),
    [genModalPipeIdsForApi, pipeList]
  );

  type CompatAlgoKey = 'omnifit' | 'optifit' | 'random';
  const COMPAT_ALGO_CONFIG: Record<CompatAlgoKey, { label: string; color: string }> = {
    omnifit: { label: 'Complete Sequence', color: '#C0392B' },
    optifit: { label: 'Optimal Fit', color: '#5D6D7E' },
    random: { label: 'Random', color: '#4A7C3F' },
  };

  const compatChartData = useMemo(() => {
    try {
      const chartData = projectSummaryHelper(projectSummaryData);
      const bins: string[] = (chartData.options?.xaxis?.categories || []).map(String);
      if (!bins.length) return null;
      return {
        bins,
        omnifit: bins.map((bin, i) => ({ bin, value: chartData.series[0]?.data[i] ?? 0 })),
        optifit: bins.map((bin, i) => ({ bin, value: chartData.series[1]?.data[i] ?? 0 })),
        random: bins.map((bin, i) => ({ bin, value: chartData.series[2]?.data[i] ?? 0 })),
      };
    } catch {
      return null;
    }
  }, [projectSummaryData]);

  const handleTriggerComplimentaryReport = () => {
    if (!projectNumber || pipeIdsForReport.length === 0) return;

    // const localId = typeof crypto !== 'undefined' && crypto.randomUUID
    //   ? crypto.randomUUID()
    //   : String(Date.now());
    // const reportName = generateReportName();
    // const generatedBy = getUsernameFromStorage();
    // const generatedAt = new Date();

    // const newReport: ReportHistoryItem = {
    //   id: localId,
    //   reportType: 'pipeCompatibility',
    //   reportName,
    //   generatedAt,
    //   generatedBy,
    //   pipeCount: pipeDisplayIdsForReport.length,
    //   pipeIds: pipeDisplayIdsForReport,
    //   status: 'Started',
    // };
    // setReportHistory((prev) => [newReport, ...prev]);

    // const basePayload: ReportHistoryApiPayload = {
    //   reportType: REPORT_TYPE_TO_API.pipeCompatibility,
    //   reportName,
    //   generatedAt: generatedAt.toISOString(),
    //   generatedBy,
    //   pipeCount: pipeDisplayIdsForReport.length,
    //   pipeIds: pipeDisplayIdsForReport,
    //   status: 'Started',
    //   pipeId: selectedPipe?.pipeId,
    //   projectId: projectId,
    //   projectNumber,
    // };
    // saveReportHistoryApi(basePayload).then((serverId) => {
    //   if (serverId) {
    //     setReportHistory((prev) =>
    //       prev.map((r) => r.id === localId ? { ...r, serverId } : r)
    //     );
    //   }
    // }).catch(() => {/* silent */ });

    setShowComplimentaryStartedModal(true);
    dispatch(
      pipefitterTriggerReportComplimentary(
        { projectNumber, pipeIds: pipeIdsForReport, pipeId: selectedPipe?.pipeId }
      )
    );
  };

  const handleGenerateReport = (
    reportType: ReportTypeTab = 'pipeData',
    overridePipeIds?: (string | number)[],
    overrideDisplayIds?: { pipeEndId: string; id: string | number }[]
  ) => {
    const idsToUse = overridePipeIds ?? pipeIdsForReport;
    const displayIds = overrideDisplayIds ?? pipeDisplayIdsForReport;
    if (!projectNumber || idsToUse.length === 0) return;

    // ── Helper to dispatch the actual report generation ──
    const dispatchGeneration = (
      localId: string,
      reportName: string,
      basePayload: ReportHistoryApiPayload,
      existingServerId?: number
    ) => {
      const updateReportStatus = (success: boolean, fileUrl?: string) => {
        setIsGenerating(false);
        setReportHistory((prev) =>
          prev.map((r) => {
            if (r.id !== localId) return r;
            const updated: ReportHistoryItem = {
              ...r,
              status: success ? 'Completed' : 'Failed',
              fileUrl: fileUrl ?? r.fileUrl,
            };
            const serverIdToUse = updated.serverId ?? existingServerId;
            if (serverIdToUse) {
              saveReportHistoryApi({
                ...basePayload,
                id: serverIdToUse,
                status: updated.status,
                fileUrl: updated.fileUrl,
              }).catch(() => {/* silent */});
            }
            return updated;
          })
        );
      };

      if (reportType === 'pipeData') {
        setIsGenerating(true);
        dispatch(
          pipefitterGenerateReportPipeData(
            { projectNumber, pipeIds: idsToUse, pipeId: selectedPipe?.pipeId, fileName: reportName, historyId: existingServerId },
            (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${reportName}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
              updateReportStatus(true);
              setReportVersionsRefreshKey((k) => k + 1);
            },
            () => updateReportStatus(false)
          )
        );
      } else if (reportType === 'pipeCompatibility') {
        setIsGenerating(true);
        dispatch(
          pipefitterGenerateReportProjectSummary(
            { projectNumber, pipeIds: idsToUse, pipeId: selectedPipe?.pipeId, reportName, historyId: existingServerId },
            (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${reportName}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
              updateReportStatus(true);
              setReportVersionsRefreshKey((k) => k + 1);
            },
            () => updateReportStatus(false)
          )
        );
      }
    };

    // ── Reopen/regenerate mode: update existing row, don't create new ──
    if (isReopenMode && reopeningReport) {
      const { id: localId, serverId: existingServerId, reportName, generatedBy } = reopeningReport;

      // Mark existing row as 'Started' and update pipe selections
      setReportHistory((prev) =>
        prev.map((r) =>
          r.id === localId
            ? { ...r, status: 'Started', pipeCount: displayIds.length, pipeIds: displayIds, fileUrl: undefined }
            : r
        )
      );

      const basePayload: ReportHistoryApiPayload = {
        reportType: REPORT_TYPE_TO_API[reportType],
        reportName,
        generatedAt: new Date().toISOString(),
        generatedBy,
        pipeCount: idsToUse.length,
        pipeIds: displayIds,
        status: 'Started',
        pipeId: selectedPipe?.pipeId,
        projectId,
        projectNumber,
      };

      // POST update with 'Started' on the existing server record
      if (existingServerId) {
        saveReportHistoryApi({ ...basePayload, id: existingServerId }).catch(() => {/* silent */});
      }

      dispatchGeneration(localId, reportName, basePayload, existingServerId);
      return;
    }

    // ── Normal mode: create a new row ──
    const localId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : String(Date.now());
    const reportName = generateReportName();
    const generatedBy = getUsernameFromStorage();
    const generatedAt = new Date();

    const newReport: ReportHistoryItem = {
      id: localId,
      reportType,
      reportName,
      generatedAt,
      generatedBy,
      pipeCount: displayIds.length,
      pipeIds: displayIds,
      status: 'Started',
    };
    setReportHistory((prev) => [newReport, ...prev]);

    const basePayload: ReportHistoryApiPayload = {
      reportType: REPORT_TYPE_TO_API[reportType],
      reportName,
      generatedAt: generatedAt.toISOString(),
      generatedBy,
      pipeCount: idsToUse.length,
      pipeIds: displayIds,
      status: 'Started',
      pipeId: selectedPipe?.pipeId,
      projectId,
      projectNumber,
    };

    saveReportHistoryApi(basePayload).then((serverId) => {
      if (serverId) {
        setReportHistory((prev) =>
          prev.map((r) => r.id === localId ? { ...r, serverId } : r)
        );
      }
      dispatchGeneration(localId, reportName, basePayload, serverId);
    }).catch(() => {
      // History save failed — still generate without historyId
      dispatchGeneration(localId, reportName, basePayload);
    });
  };

  const handleDownloadReport = (item: ReportHistoryItem) => {
    if (item.status !== 'Completed') return;

    // Multiple files → open picker modal
    if (item.fileUrls && item.fileUrls.length > 1) {
      setDownloadFilesReport(item);
      return;
    }

    // Single file → download directly from S3
    const singleUrl = item.fileUrls?.[0]?.fileUrl ?? item.fileUrl;
    const singleName = item.fileUrls?.[0]?.fileName ?? `${item.reportName || item.reportType}.pdf`;
    if (singleUrl) {
      const a = document.createElement('a');
      a.href = singleUrl;
      a.download = singleName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // Fallback: re-fetch from the pipefitter API
    if (!projectNumber) return;
    if (item.reportType === 'pipeData') {
      dispatch({
        type: PIPEFITTER_FETCH_PROJECT_REPORT,
        payload: { projectNumber },
        cb: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${projectNumber}_Pipe-Data-Report.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
      });
    } else {
      dispatch({
        type: PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
        payload: { projectNumber },
        cb: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${projectNumber}_Pipe-Compatibility-Report.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
      });
    }
  };

  const handleDeleteReport = (id: string) => {
    const report = reportHistory.find((r) => r.id === id);
    if (report) {
      setDeleteConfirmReport(report);
    }
  };

  const handleDeleteConfirmOk = () => {
    if (!deleteConfirmReport) return;
    const report = deleteConfirmReport;
    setIsDeletingReport(true);

    // Optimistically remove from local state
    setReportHistory((prev) => prev.filter((r) => r.id !== report.id));
    if (viewReportId === report.id) setViewReportId(null);

    if (report.serverId) {
      deleteReportHistoryApi([report.serverId])
        .then((ok) => {
          if (ok && projectNumber) {
            return fetchReportHistoryApi(projectNumber, projectId, selectedPipe?.pipeId)
              .then((rows) => {
                setReportHistory(rows);
                setHistoryFetchError(null);
              })
              .catch((err: any) => {
                const status = err?.response?.status ?? err?.status;
                if (status !== 410) {
                  setHistoryFetchError(err?.message ?? 'Failed to reload history.');
                }
              });
          }
        })
        .catch(() => {/* silent — local removal already applied */})
        .finally(() => {
          setIsDeletingReport(false);
          setDeleteConfirmReport(null);
        });
    } else {
      setIsDeletingReport(false);
      setDeleteConfirmReport(null);
    }
  };

  const handleDeleteConfirmCancel = () => {
    if (!isDeletingReport) {
      setDeleteConfirmReport(null);
    }
  };

  const handleEditReport = (report: ReportHistoryItem) => {
    setEditingReport(report);
    setEditReportName(report.reportName);
  };

  const handleReopenGenerate = (report: ReportHistoryItem) => {
    // Resolve actual pipeList IDs by matching pipeEndId name.
    // API-fetched reports store sequential DB ids (1,2,3…) in p.id,
    // so we cannot rely on id equality — match on pipeEndId instead.
    const resolvedIds = (report.pipeIds
      .map((p) => {
        const byName = pipeList.find((pl) => pl.pipeID === p.pipeEndId);
        if (byName) return byName.id;
        const byId = pipeList.find((pl) => pl.id === p.id);
        return byId ? byId.id : null;
      })
      .filter((id) => id != null)) as (string | number)[];

    setGenModalSelectedPipeIds(new Set(resolvedIds) as unknown as Set<string>);
    setGenIncludePipeData(report.reportType === 'pipeData');
    setGenIncludeCompatibility(report.reportType === 'pipeCompatibility');
    setIsReopenMode(true);
    setReopeningReport(report);
    setShowGenerateSummaryModal(true);
  };

  const handleSaveEdit = () => {
    if (!editingReport) return;
    const trimmed = editReportName.trim();
    if (!trimmed) return;
    setReportHistory((prev) =>
      prev.map((r) =>
        r.id === editingReport.id ? { ...r, reportName: trimmed } : r
      )
    );
    setEditingReport(null);
    setEditReportName('');
  };

  const viewReport = viewReportId ? reportHistory.find((r) => r.id === viewReportId) : null;
  const downloadVersionFile = async (row: ReportVersionRow) => {
    if (!row.fileUrl) return;
    try {
      const res = await fetch(row.fileUrl, { mode: 'cors' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.fileName || 'Report.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(row.fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden"
        >
          <div className="flex flex-col h-screen overflow-hidden">
            <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div>
                  <DialogTitle className="text-lg font-bold text-gray-900">

                    Reports – Pipefitter

                  </DialogTitle>
                  {!viewReportId && (selectedPipe ? (
                    <p className="text-sm text-gray-500 mt-0.5">
                      Project Name : {projectName} - Pipe Name : {selectedPipe.displayName ?? selectedPipe.pipeName ?? selectedPipe.pipeId}
                    </p>
                  ) : projectName ? (
                    <p className="text-sm text-gray-500 mt-0.5">{projectName}</p>
                  ) : null)}
                </div>
              </div>
              {!viewReportId && (
                <div className="flex flex-wrap items-center gap-2">
                  {/* <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setActiveVersionsTab(activeTab);
                      setShowVersionsModal(true);
                    }}
                    disabled={!projectNumber}
                  >
                    Report Versions
                  </Button> */}
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => {
                      setSummaryReportType(activeTab);
                      setIsReopenMode(false);
                      setShowGenerateSummaryModal(true);
                    }}
                    disabled={pipeList.length === 0 || isGenerating || !canReport}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Generating…
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </Button>
                </div>
              )}
            </DialogHeader>

            <div className="flex-1 flex min-h-0">
              {viewReportId && viewReport ? (
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  <Reports
                    project={project}
                    canReport={canReport}
                    selectedPipe={selectedPipe}
                    viewMode="structured"
                    initialStructuredFilter={
                      viewReport.reportType === 'pipeData' ? 'pipeData' : 'compatibility'
                    }
                  />
                </div>

              ) : (
                <>


                  {/* <aside className="w-80 border-r border-gray-200 flex flex-col flex-shrink-0 bg-gray-50/50">
                    <div className="p-3 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search pipes"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <Checkbox
                          checked={isAllSelected}
                          ref={(el) => {
                            if (el) (el as any).indeterminate = isSomeSelected;
                          }}
                          onCheckedChange={(c) => handleSelectAll(c === true)}
                          className="cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">Select All</span>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3">
                      { filteredPipeList.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">
                          {pipeList.length === 0
                            ? 'No pipes loaded. Select a pipe size on the main page.'
                            : 'No pipes match your search.'}
                        </p>
                      ) : (
                        <ul className="space-y-1">
                          {filteredPipeList.map((pipe) => (
                            <li key={pipe.id} className="flex items-center gap-2 py-1.5">
                              <Checkbox
                                checked={selectedPipeIds.has(pipe.id)}
                                onCheckedChange={(c) => handleTogglePipe(pipe.id, c === true)}
                                className="cursor-pointer"
                              />
                              <span className="text-sm font-mono text-gray-900 truncate">
                                {pipe.pipeID}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-200 flex flex-col gap-2">
                      <p className="text-sm font-medium text-gray-700">
                        Selected: {selectedCount} pipe{selectedCount !== 1 ? 's' : ''}
                      </p>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleApplyFilters(true)}
                          disabled={isReportsLoading}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </aside> */}

                  <main className="flex-1 overflow-y-auto">
                    {/* <Tabs
                      value={activeTab}
                      onValueChange={(v) => setActiveTab(v as ReportTypeTab)}
                      className="w-full"
                    >
                      <div className="sticky top-0 bg-background rounded-t-lg z-10 border-b border-border shadow-sm -mx-4 sm:-mx-6 px-4 sm:px-6 mb-4">
                        <div className="flex items-center justify-between px-1 md:px-4 py-2 md:py-3 gap-2">
                          <TabsList className="h-auto justify-start gap-2 md:gap-4 lg:gap-8 border-b-0 bg-transparent flex-wrap p-0">
                            {REPORT_MODAL_TABS.map(({ key, label, shortLabel, icon: Icon }) => (
                              <TabsTrigger
                                key={key}
                                value={key}
                                className={PIPEFITTER_TAB_TRIGGER_CLASS}
                              >
                                <div className="flex items-center gap-1 md:gap-2">
                                  <Icon className="h-3 w-3 md:h-4 md:w-4" />
                                  <span className="hidden sm:inline">{label}</span>
                                  <span className="sm:hidden">{shortLabel}</span>
                                </div>
                              </TabsTrigger>
                            ))}
                          </TabsList>
                        </div>
                      </div>
                      <TabsContent value="pipeData" className="mt-4 focus-visible:outline-none">
                        {isReportsLoading ? (
                          <div className="flex flex-col items-center justify-center py-12 gap-3">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                            <p className="text-sm text-gray-500">Loading pipe data...</p>
                          </div>
                        ) : (
                          <PipeData
                            pipeStatistics={pipeStatistics}
                            pipeDataGraph={pipeDataGraph}
                            isLoading={isReportsLoading}
                            handleSwitchChange={setShowGraphicalView}
                            showGraphicalView={showGraphicalView}
                            projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
                            canReport={canReport}
                            isShowIndividualPipeDetails={true}
                            selectedPipe={selectedPipe}
                          />
                        )}
                      </TabsContent>
                      <TabsContent value="pipeCompatibility" className="mt-4 focus-visible:outline-none">

                        <PipeCompatibility
                          projectSummaryData={projectSummaryData}
                          projectNumber={projectNumber}
                          isLoading={isReportsLoading}
                          canReport={canReport}
                        />
                      </TabsContent>
                    </Tabs> */}
                      {/* ── Pipe Compatibility CTA ── */}
                      {/* <div className="px-4 sm:px-6 pb-8">
                      <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                          <h3 className="text-base font-bold text-gray-900">Pipe Compatibility Report</h3>
                          <p className="text-sm text-gray-500 mt-1 max-w-lg leading-relaxed">
                            Compare pipe end compatibility using multiple matching algorithms to find the optimal fit for welding.
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {(Object.entries(COMPAT_ALGO_CONFIG) as [CompatAlgoKey, { label: string; color: string }][]).map(([key, cfg]) => (
                              <span
                                key={key}
                                className="px-3 py-1 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-700"
                              >
                                {cfg.label}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 ml-auto">
                          <Button
                            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 gap-2"
                            onClick={() => setShowCompatFullView(true)}
                          >
                            <Link2 className="h-4 w-4" />
                            Go to Compatibility
                          </Button>
                          <Button
                            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 gap-2"
                            onClick={() => handleTriggerComplimentaryReport()}
                          >
                            <Link2 className="h-4 w-4" />
                            Run Compatibility
                          </Button>
                        </div>
                      
                      </div>
                    </div> */}

                    {/* ── Recent Reports Table ── */}
                    <div className="px-4 sm:px-6 py-6">
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/70">
                          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <History className="h-4 w-4 text-gray-500" />
                            Recent Reports
                          </h3>
                          <span className="text-xs text-gray-400">
                            {reportHistory.length} report{reportHistory.length !== 1 ? 's' : ''}
                            {selectedPipe?.pipeId ? ' for this pipe size' : ''}
                          </span>
                        </div>

                        {isHistoryLoading ? (
                          <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
                            <p className="text-sm">Loading report history…</p>
                          </div>
                        ) : historyFetchError ? (
                          <div className="flex flex-col items-center justify-center py-12 gap-2">
                            <div className="flex items-center gap-2 text-red-500">
                              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                              <p className="text-sm font-medium">Failed to load report history</p>
                            </div>
                            <p className="text-xs text-gray-400 max-w-xs text-center">{historyFetchError}</p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-2 text-xs"
                              onClick={() => {
                                setHistoryFetchError(null);
                                setIsHistoryLoading(true);
                                fetchReportHistoryApi(projectNumber, projectId, selectedPipe?.pipeId)
                                  .then(setReportHistory)
                                  .catch((err: any) => {
                                    const status = err?.response?.status ?? err?.status;
                                    setHistoryFetchError(
                                      status === 410 ? null : (err?.message ?? 'Fetch failed')
                                    );
                                  })
                                  .finally(() => setIsHistoryLoading(false));
                              }}
                            >
                              Retry
                            </Button>
                          </div>
                        ) : reportHistory.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
                            <FileText className="h-10 w-10 text-gray-200" />
                            <p className="text-sm">No reports found for this project{selectedPipe?.pipeId ? ' and pipe size' : ''}.</p>
                            <p className="text-xs text-gray-400">Use the Generate Report button above to create one.</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <Table className="min-w-[800px]">
                              <TableHeader className="sticky top-0 z-10 bg-white">
                                <TableRow>
                                  <TableHead className="whitespace-nowrap">Report Name</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Pipes</TableHead>
                                  <TableHead className="whitespace-nowrap">Generated</TableHead>
                                  <TableHead className="whitespace-nowrap">Generated By</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Reports</TableHead>
                                  <TableHead className="w-24" />
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportHistory.map((report) => (
                                  <TableRow key={report.id}>
                                    {/* Report Name */}
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        {report.reportType === 'pipeData' ? (
                                          <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                        ) : (
                                          <Database className="h-4 w-4 text-purple-500 flex-shrink-0" />
                                        )}
                                        <span className="font-mono text-xs font-semibold text-blue-700 tracking-wide">
                                          {report.reportName}
                                        </span>
                                      </div>
                                    </TableCell>

                                    {/* Type */}
                                    <TableCell>
                                      <span
                                        className={cn(
                                          'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold',
                                          report.reportType === 'pipeData'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-purple-100 text-purple-800'
                                        )}
                                      >
                                        {report.reportType === 'pipeData' ? 'Pipe Data' : 'Compatibility'}
                                      </span>
                                    </TableCell>

                                    {/* Pipes */}
                                    <TableCell>
                                      <div className="flex flex-wrap gap-1">
                                        {report.pipeIds.slice(0, 3).map((p) => (
                                          <span
                                            key={p.id}
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 border border-gray-200 text-gray-700"
                                          >
                                            {p.pipeEndId}
                                          </span>
                                        ))}
                                        {report.pipeIds.length > 3 && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs text-gray-400 bg-gray-100">
                                            +{report.pipeIds.length - 3} more
                                          </span>
                                        )}
                                      </div>
                                    </TableCell>

                                    {/* Generated */}
                                    <TableCell className="text-gray-600 whitespace-nowrap text-xs">
                                      {formatReportDate(report.generatedAt)}
                                    </TableCell>

                                    {/* Generated By */}
                                    <TableCell className="text-gray-600 text-xs whitespace-nowrap">
                                      {report.generatedBy}
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>
                                      <div className="flex items-center gap-1.5">
                                        {report.status === 'Started' || report.status === 'Processing' ? (
                                          <Loader2 className="h-3 w-3 animate-spin text-yellow-500" />
                                        ) : (
                                          <span
                                            className={cn(
                                              'inline-block w-2 h-2 rounded-full flex-shrink-0',
                                              report.status === 'Completed' ? 'bg-green-500' : 'bg-red-500'
                                            )}
                                          />
                                        )}
                                        <span
                                          className={cn(
                                            'text-xs font-medium',
                                            report.status === 'Completed'
                                              ? 'text-green-700'
                                              : report.status === 'Started' || report.status === 'Processing'
                                                ? 'text-yellow-600'
                                                : 'text-red-600'
                                          )}
                                        >
                                          {report.status}
                                        </span>
                                      </div>
                                    </TableCell>

                                    {/* Reports — download */}
                                    <TableCell>
                                      {report.status === 'Completed' && (
                                        <Button
                                          onClick={() => handleDownloadReport(report)}
                                          title={report.fileUrl ? 'Download PDF from S3' : 'Download PDF'}
                                          //className="flex items-center gap-1 text-gray-400 hover:text-blue-600 transition-colors"
                                          variant="outline"
                                          size="sm"
                                          className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                        >
                                          <Download className="h-4 w-4 flex-shrink-0" />

                                        </Button>
                                      )}
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 w-7 p-0 text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                                          onClick={() => handleReopenGenerate(report)}
                                          title="Re-generate with same pipes"
                                        >
                                          <Edit className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                          onClick={() => handleDeleteReport(report.id)}
                                          title="Remove from list"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    </div>

                  
                  </main>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showVersionsModal && (
        <PipefitterReportVersionsModal
          isOpen={showVersionsModal}
          onClose={() => setShowVersionsModal(false)}
          project={project}
          projectName={projectName}
          projectNumber={projectNumber}
          projectId={projectId}
          selectedPipe={selectedPipe}
          selectedPipeStatusIds={Array.from(selectedPipeIds)}
          versionsRefreshKey={reportVersionsRefreshKey}
        />
      )}

      {/* ── Compatibility Full-View Modal ── */}
      <Dialog open={showCompatFullView} onOpenChange={(open) => !open && setShowCompatFullView(false)}>
        <DialogContent className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden">
          <div className="flex flex-col h-screen overflow-hidden">
            {/* Header */}
            <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => setShowCompatFullView(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div>
                  <DialogTitle className="text-lg font-bold text-gray-900">
                    Pipe Compatibility – Algorithm View
                  </DialogTitle>
                  <p className="text-sm text-gray-500 mt-0.5">
                    HILO distribution across matching algorithms
                  </p>
                </div>
              </div>
              {/* Overall / Individual toggle */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mr-2">
                <button
                  onClick={() => setCompatViewMode('overall')}
                  className={cn(
                    'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
                    compatViewMode === 'overall' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  Overall
                </button>
                <button
                  onClick={() => setCompatViewMode('individual')}
                  className={cn(
                    'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
                    compatViewMode === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  Individual
                </button>
              </div>
            </DialogHeader>

            {/* Body */}
            <main className="flex-1 overflow-y-auto bg-gray-50/40">
              <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
                {!compatChartData ? (
                  isReportsLoading ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                      <p className="text-sm text-gray-500">Loading compatibility data…</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
                      <Database className="h-14 w-14 text-gray-200" />
                      <p className="text-base font-medium text-gray-500">No compatibility data available yet.</p>
                      <p className="text-sm text-gray-400">Generate a Compatibility report first to see results here.</p>
                    </div>
                  )
                ) : compatViewMode === 'overall' ? (
                  /* ── Overall: PipeCompatibility + all 3 charts stacked ── */
                  <div className="space-y-6">
                    <PipeCompatibility
                      projectSummaryData={projectSummaryData}
                      projectNumber={projectNumber}
                      isLoading={isReportsLoading}
                      canReport={canReport}
                    />
                  </div>
                ) : (
                  /* ── Individual: tabs + single chart ── */
                  <>
                    <div className="flex border-b border-gray-200 bg-white rounded-t-lg overflow-hidden shadow-sm">
                      {(Object.entries(COMPAT_ALGO_CONFIG) as [CompatAlgoKey, { label: string; color: string }][]).map(([key, cfg]) => (
                        <button key={key} onClick={() => setCompatAlgoTab(key)}
                          className={cn('flex-1 px-5 py-4 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center justify-center gap-2',
                            compatAlgoTab === key ? 'bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50')}
                          style={compatAlgoTab === key ? { borderBottomColor: cfg.color, color: cfg.color } : {}}
                        >
                          <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                    <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg shadow-sm p-8">
                      <div className="flex items-center gap-3 mb-8">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: COMPAT_ALGO_CONFIG[compatAlgoTab].color }} />
                        <span className="text-base font-semibold text-gray-800">{COMPAT_ALGO_CONFIG[compatAlgoTab].label}</span>
                        <span className="text-sm text-gray-400 ml-auto">
                          Total joints: <strong className="text-gray-700">{compatChartData[compatAlgoTab].reduce((s, d) => s + d.value, 0)}</strong>
                        </span>
                      </div>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={compatChartData[compatAlgoTab]} margin={{ top: 8, right: 32, left: 8, bottom: 20 }} barCategoryGap="30%">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                          <XAxis dataKey="bin" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false}
                            label={{ value: 'HILO (mm)', position: 'insideBottom', offset: -10, fontSize: 12, fill: '#9ca3af' }} />
                          <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false}
                            label={{ value: 'Joints', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#9ca3af' }} />
                          <RechartsTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                            formatter={(value: number) => [`${value} joints`, COMPAT_ALGO_CONFIG[compatAlgoTab].label]}
                            labelFormatter={(label) => `HILO: ${label} mm`}
                            contentStyle={{ fontSize: 13, borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {compatChartData[compatAlgoTab].map((_, idx) => (
                              <Cell key={idx} fill={COMPAT_ALGO_CONFIG[compatAlgoTab].color} fillOpacity={0.85} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <p className="text-center text-sm text-green-600 font-medium mt-6">
                        ✓ All joints within 1.5 mm HILO project constraint
                      </p>
                    </div>
                  </>
                )}
              </div>
            </main>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Files Modal */}
      <Dialog open={!!downloadFilesReport} onOpenChange={(open) => { if (!open) setDownloadFilesReport(null); }}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-gray-100">
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Download className="h-4 w-4 text-blue-500" />
              Download Report
            </DialogTitle>
            {downloadFilesReport && (
              <p className="text-xs text-gray-500 mt-1 font-mono">
                {downloadFilesReport.reportName}
              </p>
            )}
          </DialogHeader>

          {downloadFilesReport?.fileUrls && (
            <ul className="divide-y divide-gray-100">
              {[...downloadFilesReport.fileUrls].reverse().map((version, idx) => {
                const isLatest = idx === 0;
                return (
                  <li key={version.id} className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors">
                    <FileText className={`h-4 w-4 flex-shrink-0 ${isLatest ? 'text-blue-500' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <span className="block text-xs font-mono text-gray-700 truncate" title={version.fileName}>
                        {version.fileName}
                      </span>
                      {version.createdAt && (
                        <span className="text-xs text-gray-400">
                          {formatReportDate(new Date(version.createdAt))}
                        </span>
                      )}
                    </div>
                    {isLatest && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 flex-shrink-0">
                        Latest
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0"
                      title={`Download ${version.fileName}`}
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = version.fileUrl;
                        a.download = version.fileName;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setDownloadFilesReport(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Report Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmReport}
        onOpenChange={(open) => {
          if (!open && !isDeletingReport) {
            setDeleteConfirmReport(null);
          }
        }}
      >
        <DialogContent className="max-w-[800px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle className="text-xl font-semibold">Confirm Delete</DialogTitle>
          </div>
          <div className="mb-6">
            {isDeletingReport && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-md">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-600 border-t-transparent" />
                <span className="text-sm text-gray-600">Deleting and refreshing...</span>
              </div>
            )}
            <p className="text-gray-700 mb-3">
              Are you sure you want to{' '}
              <span className="font-semibold text-red-600">delete</span> this report from history?
            </p>
            {deleteConfirmReport && (
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <p className="text-sm text-gray-600 mb-1">Report details:</p>
                <div className="text-sm text-gray-700 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0" />
                    <span className="font-medium">{deleteConfirmReport.reportName}</span>
                  </div>
                  <div className="pl-4 text-xs text-gray-500">
                    Type: {deleteConfirmReport.reportType === 'pipeData' ? 'Pipe Data Report' : 'Compatibility Report'}
                    {' · '}
                    Status: {deleteConfirmReport.status}
                  </div>
                </div>
              </div>
            )}
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                <span className="font-semibold">Warning:</span> This action is irreversible.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleDeleteConfirmCancel}
              disabled={isDeletingReport}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirmOk}
              disabled={isDeletingReport}
              className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
            >
              {isDeletingReport ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Report Name Dialog */}
      <Dialog open={!!editingReport} onOpenChange={(open) => { if (!open) { setEditingReport(null); setEditReportName(''); } }}>
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Report Name
            </DialogTitle>
          </DialogHeader>
          {editingReport && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-gray-600">
                Type:{' '}
                <span className="font-medium text-gray-900">
                  {editingReport.reportType === 'pipeData' ? 'Pipe Data' : 'Compatibility'}
                </span>
              </p>
              <div className="space-y-2">
                <Label htmlFor="edit-report-name">Report Name</Label>
                <Input
                  id="edit-report-name"
                  value={editReportName}
                  onChange={(e) => setEditReportName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); }}
                  placeholder="Enter report name"
                  className="w-full"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => { setEditingReport(null); setEditReportName(''); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={!editReportName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showGenerateSummaryModal} onOpenChange={(open) => { setShowGenerateSummaryModal(open); if (!open) { setIsReopenMode(false); setReopeningReport(null); } }}>
        <DialogContent className="max-w-[1200px] w-[95vw] p-0 gap-0 overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-gray-100">
            <DialogTitle className="text-base font-bold text-gray-900">Generate Report</DialogTitle>
            <p className="text-sm text-gray-500 mt-0.5">
              Select pipes and choose which reports to generate.
              {projectName && (
                <span className="ml-1 text-gray-700 font-medium">{projectName}</span>
              )}
              {projectNumber && (
                <span className="text-gray-400 font-normal ml-1">({projectNumber})</span>
              )}
            </p>
          </DialogHeader>

          {/* Body — split layout */}
          <div className="flex min-h-0" style={{ height: '680px' }}>

            {/* ── Left: Pipe Selector ── */}
            <div className="w-64 flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50/50">
              <div className="px-3 pt-3 pb-2 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Select Pipes</p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    placeholder="Search pipes"
                    value={genModalPipeSearch}
                    onChange={(e) => setGenModalPipeSearch(e.target.value)}
                    className="pl-8 h-8 text-xs"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    checked={genModalIsAllSelected}
                    ref={(el) => {
                      if (el) (el as any).indeterminate = genModalIsSomeSelected;
                    }}
                    onCheckedChange={(c) => handleGenModalSelectAll(c === true)}
                    className="cursor-pointer h-3.5 w-3.5"
                  />
                  <span className="text-xs font-medium text-gray-600">Select All</span>
                  {genModalSelectedCount > 0 && (
                    <span className="ml-auto text-xs text-blue-600 font-semibold">{genModalSelectedCount} selected</span>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-2">
                {genModalFilteredPipeList.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">
                    {pipeList.length === 0 ? 'No pipes loaded.' : 'No pipes match.'}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {genModalFilteredPipeList.map((pipe) => (
                      <li key={pipe.id} className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-blue-50/60 transition-colors">
                        <Checkbox
                          checked={genModalSelectedPipeIds.has(pipe.id)}
                          onCheckedChange={(c) => handleGenModalTogglePipe(pipe.id, c === true)}
                          className="cursor-pointer h-3.5 w-3.5"
                        />
                        <span className="text-xs font-mono text-gray-900 truncate">{pipe.pipeID}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-500">
                  {genModalSelectedCount > 0
                    ? <><span className="font-semibold text-blue-600">{genModalSelectedCount}</span> pipe{genModalSelectedCount !== 1 ? 's' : ''} selected</>
                    : <><span className="font-semibold">All {pipeList.length}</span> pipe{pipeList.length !== 1 ? 's' : ''} (none selected)</>
                  }
                </p>
              </div>
            </div>

            {/* ── Right: Report Type Selection ── */}
            <div className="flex-1 flex flex-col overflow-y-auto">
              <div className="p-5 space-y-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Choose Report Types</p>

                {/* Pipe Data Report card */}
                <label
                  className={cn(
                    'flex items-start gap-3 p-4 rounded-lg border-2 transition-all',
                    isReopenMode
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer',
                    genIncludePipeData
                      ? 'border-blue-400 bg-blue-50/60'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <Checkbox
                    checked={genIncludePipeData}
                    onCheckedChange={(c) => !isReopenMode && setGenIncludePipeData(c === true)}
                    disabled={isReopenMode}
                    className="cursor-pointer mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-900">Pipe Data Report</span>
                      <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        Pipe Data
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                      Generates dimensional measurement data (ID &amp; OD) per pipe end.
                      Downloaded immediately as PDF.
                    </p>
                  </div>
                </label>

                {/* Compatibility Report card */}
                <label
                  className={cn(
                    'flex items-start gap-3 p-4 rounded-lg border-2 transition-all',
                    isReopenMode
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer',
                    genIncludeCompatibility
                      ? 'border-purple-400 bg-purple-50/60'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <Checkbox
                    checked={genIncludeCompatibility}
                    onCheckedChange={(c) => !isReopenMode && setGenIncludeCompatibility(c === true)}
                    disabled={isReopenMode}
                    className="cursor-pointer mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-900">Pipe Compatibility Report</span>
                      <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        Compatibility
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                      Compares pipe end compatibility using matching algorithms
                      (Complete Sequence, Optimal Fit, Random) to find the best weld pairing.
                    </p>
                  </div>
                </label>

                {/* Scope summary */}
                <div
                  className={cn(
                    'rounded-lg border p-3 space-y-2 mt-2',
                    genModalSelectedCount > 0
                      ? 'border-blue-200 bg-blue-50/40'
                      : 'border-amber-200 bg-amber-50/40'
                  )}
                >
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Report Scope</p>
                  {genModalSelectedCount > 0 ? (
                    <>
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold text-blue-700">{genModalSelectedCount}</span>{' '}
                        pipe{genModalSelectedCount !== 1 ? 's' : ''} selected — report will include only these pipes.
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {genModalPipeDisplayIds.slice(0, 8).map((p) => (
                          <span
                            key={p.id}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-white border border-gray-200 text-gray-700"
                          >
                            {p.pipeEndId}
                          </span>
                        ))}
                        {genModalPipeDisplayIds.length > 8 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-gray-400 bg-white border border-gray-200">
                            +{genModalPipeDisplayIds.length - 8} more
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-gray-700">
                      No pipes selected — report will include{' '}
                      <span className="font-semibold">all {pipeList.length}</span>{' '}
                      pipe{pipeList.length !== 1 ? 's' : ''} for this pipe size.
                    </p>
                  )}

                  {selectedPipe && (
                    <p className="text-xs text-gray-500 pt-1 border-t border-gray-100 mt-2">
                      Pipe size:{' '}
                      <span className="font-medium text-gray-700">
                        {selectedPipe.displayName ?? selectedPipe.pipeName ?? selectedPipe.pipeId}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-400">
              {[genIncludePipeData && 'Pipe Data', genIncludeCompatibility && 'Compatibility']
                .filter(Boolean)
                .join(' + ') || 'No report selected'}
              {(genIncludePipeData || genIncludeCompatibility) && (
                <span className="ml-1">
                  — {genIncludePipeData && genIncludeCompatibility ? '2 reports' : '1 report'} will be generated
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowGenerateSummaryModal(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleConfirmGenerate}
                disabled={
                  (!genIncludePipeData && !genIncludeCompatibility) ||
                  genModalPipeIdsForApi.length === 0 ||
                  isGenerating
                }
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    Generating…
                  </>
                ) : (
                  <>
                    Generate{' '}
                    {genIncludePipeData && genIncludeCompatibility
                      ? '2 Reports'
                      : genIncludePipeData
                        ? 'Pipe Data Report'
                        : genIncludeCompatibility
                          ? 'Compatibility Report'
                          : 'Report'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>



      <Dialog open={showComplimentaryStartedModal} onOpenChange={setShowComplimentaryStartedModal}>
        <DialogContent className="max-w-lg min-h-[420px] flex flex-col gap-6 sm:min-h-[260px]">
          <DialogHeader className="flex-1 space-y-3 text-left">
            <DialogTitle>Pipe compatibility Calculation</DialogTitle>
            <DialogDescription asChild>
              <div className="text-gray-600 text-left leading-relaxed space-y-3 text-sm">
                <p>
                  Complimentary report generation has started. We will notify you when it is ready — you do not need
                  to wait on this screen.
                </p>
                <p>The report will be available in the Reports section of the project.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-auto sm:justify-end">
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setShowComplimentaryStartedModal(false)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
